import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { FilterSelect, SearchInput, StateMessage } from '@/components/ui/controls';
import { PageHeader } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import { StageTag } from '@/components/ui/stage';
import { NotYetInfo, notYet, notYetText } from '@/components/ui/not-yet';
import { btn } from '@/components/ui/styles';
import type { LeadFacets, LeadPage as LeadPageData } from '@/types/api';
import { formatDate, initials, shownEmail } from '@/utils/format';
import { STAGES, sourceLabel, stageLabel } from '@/utils/labels';
import { useApi, useDebounced } from '@/utils/use-api';
import type { Sort } from '@/utils/use-sort';

import LeadPanel from './lead-panel';
import NewLeadPanel from './new-lead';

const PAGE_SIZE = 50;

// The columns the server sorts the list by (GET /leads?sort=), and whether a first click
// puts the largest or latest first. With no sort the list is the newest first.
const FIRST_DESC: Record<string, boolean> = { name: false, stage: false, casinos: true, added: true };

/**
 * Leads (LEAD-*, CAS-3): everyone who registered, wrote to us or is in a Telegram
 * group. Filters live in the URL so a view can be shared; ?lead=<id> opens the
 * detail panel.
 */
const LeadsPage = () => {
  const [params, setParams] = useSearchParams();
  const stage = params.get('stage') ?? '';
  const blocked = params.get('blocked') === '1';
  const source = params.get('source') ?? '';
  const page = Math.max(0, Number(params.get('page') ?? 0) || 0);
  const sortParam = params.get('sort') ?? '';
  const sort: Sort = sortParam ? { key: sortParam.replace(/^-/, ''), desc: sortParam.startsWith('-') } : { key: 'added', desc: true };
  const selected = params.get('lead');

  // The search box updates the URL only once typing pauses.
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [creating, setCreating] = useState(false);
  const debounced = useDebounced(query);
  const update = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  // A click sorts by the column, a second one reverses it; either way back to the first page.
  const sortBy = (key: string) => {
    const desc = sort.key === key ? !sort.desc : FIRST_DESC[key];
    const next = (desc ? '-' : '') + key;
    update({ sort: next === '-added' ? null : next, page: null });
  };
  useEffect(() => {
    if ((params.get('q') ?? '') !== debounced) update({ q: debounced || null, page: null });
    // Only the debounced text drives this; `params` and `update` change on every navigation.
  }, [debounced]);

  const list = useApi<LeadPageData>('/leads', {
    q: params.get('q') ?? undefined,
    stage: stage || undefined,
    blocked: blocked ? 1 : undefined,
    source: source || undefined,
    sort: sortParam || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const facets = useApi<LeadFacets>('/leads/facets');

  const filtered = Boolean(params.get('q') || stage || source || blocked);
  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  return (
    <div className={classNames('transition-[padding]', selected && 'xl:pr-[452px]')}>
      <PageHeader
        title="Leads"
        description="Everyone who registered, wrote to us or joined a Telegram group with the team."
        actions={
          <>
            <span className="relative inline-flex">
              <Link to="/leads/suggestions" className={classNames(btn.secondary, notYet)}>
                AI suggestions
              </Link>
              <NotYetInfo what="ai" className="absolute -right-1.5 -top-1.5 bg-surface" />
            </span>
            <button
              type="button"
              className={btn.primary}
              onClick={() => {
                update({ lead: null });
                setCreating(true);
              }}
            >
              New lead
            </button>
          </>
        }
      >
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput value={query} onChange={setQuery} label="Search leads" placeholder="Search email, @handle, name or casino" />
          <FilterSelect
            label="Stage"
            value={blocked ? 'blocked' : stage}
            onChange={(v) => update(v === 'blocked' ? { stage: null, blocked: '1', page: null } : { stage: v || null, blocked: null, page: null })}
          >
            <option value="">All stages</option>
            {(facets.data?.stages ?? STAGES.map((s) => ({ value: s.value, count: 0 }))).map((f) => (
              <option key={f.value} value={f.value}>
                {stageLabel(f.value)} ({f.count})
              </option>
            ))}
            {/* Blocked is a tag on Configuring or Clarifications, not a stage (Q4). */}
            <option value="blocked">Blocked ({facets.data?.blocked ?? 0})</option>
          </FilterSelect>
          <FilterSelect label="Source" value={source} onChange={(v) => update({ source: v || null, page: null })}>
            <option value="">All sources</option>
            {(facets.data?.sources ?? []).map((f) => (
              <option key={f.value} value={f.value}>
                {sourceLabel(f.value)} ({f.count})
              </option>
            ))}
          </FilterSelect>
          {filtered && (
            <button
              type="button"
              className="min-h-11 text-sm text-fg-muted underline-offset-2 hover:text-fg hover:underline md:min-h-0"
              onClick={() => {
                setQuery('');
                update({ q: null, stage: null, source: null, blocked: null, page: null });
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </PageHeader>
      {creating && (
        <NewLeadPanel
          onClose={() => setCreating(false)}
          onOpen={(id, created) => {
            setCreating(false);
            if (created) list.reload();
            update({ lead: id });
          }}
        />
      )}

      {list.error && !list.data ? (
        <StateMessage title="Leads could not be loaded" tone="error">
          {list.error}
        </StateMessage>
      ) : list.data && items.length === 0 ? (
        <StateMessage title={filtered ? 'No lead matches these filters' : 'No leads yet'}>
          {filtered ? 'Try another search or clear the filters.' : 'Leads appear here once the Owner Hub sync has run.'}
        </StateMessage>
      ) : (
        <div className={classNames('overflow-hidden rounded-card bg-surface shadow-e1 transition-opacity', list.loading && list.data && 'opacity-60')}>
          <div className="overflow-x-auto">
            {/* On a phone (UI-1) only the Lead column shows, with the stage, source and date folded into it. */}
            <table className="w-full text-left text-sm md:min-w-[860px]">
              <thead className="border-b border-line text-xs text-fg-muted">
                <tr>
                  <SortTh label="Lead" column="name" sort={sort} onSort={sortBy} />
                  <th scope="col" className="hidden px-4 py-3 font-medium md:table-cell">Telegram</th>
                  <th scope="col" className="hidden px-4 py-3 font-medium md:table-cell">Stage</th>
                  <th scope="col" className="hidden px-4 py-3 font-medium md:table-cell">Source</th>
                  <th scope="col" className="hidden px-4 py-3 font-medium md:table-cell">
                    <span className="inline-flex items-center gap-1.5">
                      <span className={notYet}>AI summary</span>
                      <NotYetInfo what="ai" small />
                    </span>
                  </th>
                  <SortTh label="Casinos" column="casinos" sort={sort} onSort={sortBy} right className="hidden md:table-cell" />
                  <SortTh label="Added" column="added" sort={sort} onSort={sortBy} className="hidden md:table-cell" />
                </tr>
              </thead>
              <tbody>
                {!list.data &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="border-b border-line last:border-0">
                      <td colSpan={7} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-layer" />
                      </td>
                    </tr>
                  ))}
                {items.map((l) => (
                  <tr
                    key={l.id}
                    tabIndex={0}
                    aria-selected={selected === l.id}
                    onClick={() => update({ lead: l.id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        update({ lead: l.id });
                      }
                    }}
                    className={classNames(
                      'cursor-pointer border-b border-line align-top transition-colors last:border-0 focus:outline-none focus-visible:bg-surface-hover',
                      selected === l.id ? 'bg-brand/10' : 'hover:bg-surface-hover',
                    )}
                  >
                    <td className="max-w-0 px-4 py-3 md:max-w-none">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-inset text-xs text-fg-muted">
                          {initials(l.full_name, shownEmail(l.email))}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-fg-strong">{shownEmail(l.email) || <span className="text-fg-muted">No email</span>}</p>
                          <p className="truncate text-xs text-fg-muted">{[l.full_name, l.company].filter(Boolean).join(' · ') || '—'}</p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted md:hidden">
                            <StageTag stage={l.stage} blocked={Boolean(l.blocked_at)} />
                            <span>{sourceLabel(l.source)}</span>
                            <span className="tabular-nums">{formatDate(l.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden whitespace-nowrap px-4 py-3 text-fg-muted md:table-cell">{l.telegram_handle ? `@${l.telegram_handle}` : '—'}</td>
                    <td className="hidden px-4 py-3 md:table-cell">
                      <StageTag stage={l.stage} blocked={Boolean(l.blocked_at)} />
                    </td>
                    <td className={classNames('hidden whitespace-nowrap px-4 py-3 md:table-cell', l.source ? 'text-fg' : 'text-fg-faint')}>{sourceLabel(l.source)}</td>
                    <td className={classNames('hidden max-w-xs px-4 py-3 text-fg-muted md:table-cell', notYet)} title={notYetText('ai')}>
                      <p className="line-clamp-2">{l.summary || '—'}</p>
                    </td>
                    <td className="hidden whitespace-nowrap px-4 py-3 text-right tabular-nums text-fg md:table-cell">
                      {l.casinos ? (
                        <span title={`${l.live_casinos} live of ${l.casinos}`}>
                          {l.live_casinos}
                          <span className="text-fg-faint"> / {l.casinos}</span>
                        </span>
                      ) : (
                        <span className="text-fg-faint">—</span>
                      )}
                    </td>
                    <td className="hidden whitespace-nowrap px-4 py-3 tabular-nums text-fg-muted md:table-cell">{formatDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm text-fg-muted">
              <span>
                {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total}
              </span>
              <span className="flex gap-2">
                <button type="button" disabled={page === 0} onClick={() => update({ page: page > 1 ? String(page - 1) : null })} className="h-11 rounded-lg bg-control px-4 shadow-ctl transition hover:shadow-ctl-hover active:translate-y-px active:shadow-ctl-press hover:bg-surface-hover disabled:opacity-40 disabled:hover:shadow-ctl disabled:active:translate-y-0 disabled:active:shadow-ctl md:h-auto md:px-3 md:py-1">
                  Previous
                </button>
                <button type="button" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => update({ page: String(page + 1) })} className="h-11 rounded-lg bg-control px-4 shadow-ctl transition hover:shadow-ctl-hover active:translate-y-px active:shadow-ctl-press hover:bg-surface-hover disabled:opacity-40 disabled:hover:shadow-ctl disabled:active:translate-y-0 disabled:active:shadow-ctl md:h-auto md:px-3 md:py-1">
                  Next
                </button>
              </span>
            </div>
          )}
          {total > 0 && total <= PAGE_SIZE && (
            <p className="border-t border-line px-4 py-3 text-sm text-fg-muted">
              {total} {total === 1 ? 'lead' : 'leads'}
            </p>
          )}
        </div>
      )}

      {selected && <LeadPanel id={selected} onClose={() => update({ lead: null })} />}
    </div>
  );
};

export default LeadsPage;
